import { makeAutoObservable } from "mobx"

import knowledgeService from "@services/knowledge.service"
import {
  BrandExpertMapping,
  BrandExpertResolution,
  BrandExpertRole,
  RoutableBrand,
} from "@framework/types/brand-expert"

/**
 * LAB-47: brand -> expert routing.
 *
 * Serves both the "Ask an Expert" form (routable brands, and a preview of who a
 * question will be sent to) and the admin maintenance screen where the mapping
 * is kept up to date.
 */
export class BrandExpertStore {
  constructor() {
    makeAutoObservable(this)
  }

  brands: RoutableBrand[] = []

  isBrandsLoading = false

  mappings: BrandExpertMapping[] = []

  isMappingsLoading = false

  isActionLoading = false

  error: string | null = null

  /** companyId -> who the question would go to. Cached so reopening is instant. */
  resolutions: Record<string, BrandExpertResolution> = {}

  get brandOptions() {
    return this.brands.map((brand) => ({ name: brand.id, value: brand.name }))
  }

  /** Mappings grouped by brand, primary first, for the admin table. */
  get mappingsByBrand(): {
    companyId: string
    companyName: string
    experts: BrandExpertMapping[]
  }[] {
    const groups = new Map<string, BrandExpertMapping[]>()

    this.mappings.forEach((mapping) => {
      const bucket = groups.get(mapping.companyId) || []
      bucket.push(mapping)
      groups.set(mapping.companyId, bucket)
    })

    return [...groups.entries()]
      .map(([companyId, experts]) => ({
        companyId,
        companyName: experts[0]?.companyName || "",
        experts,
      }))
      .sort((a, b) => a.companyName.localeCompare(b.companyName))
  }

  loadBrands = async () => {
    if (this.brands.length || this.isBrandsLoading) return

    this.isBrandsLoading = true
    try {
      const response = await knowledgeService.getRoutableBrands()
      this.brands = response.data.data || []
    } catch (error) {
      this.error = "Failed to load brands"
    } finally {
      this.isBrandsLoading = false
    }
  }

  resolveExpert = async (
    companyId: string
  ): Promise<BrandExpertResolution | null> => {
    if (!companyId) return null
    if (this.resolutions[companyId]) return this.resolutions[companyId]

    try {
      const response = await knowledgeService.resolveBrandExpert(companyId)
      const resolution = response.data.data
      if (resolution) this.resolutions[companyId] = resolution
      return resolution ?? null
    } catch (error) {
      // A failed preview must not block submitting - the backend resolves the
      // expert again on submit anyway.
      return null
    }
  }

  loadMappings = async () => {
    this.isMappingsLoading = true
    this.error = null
    try {
      const response = await knowledgeService.getBrandExperts()
      this.mappings = response.data.data || []
    } catch (error) {
      this.error = "Failed to load the brand to expert mapping"
    } finally {
      this.isMappingsLoading = false
    }
  }

  addMapping = async (payload: {
    companyId: string
    userId: string
    role?: BrandExpertRole
  }): Promise<boolean> => {
    this.isActionLoading = true
    this.error = null
    try {
      await knowledgeService.createBrandExpert(payload)
      await this.loadMappings()
      this.invalidateBrandCaches()
      return true
    } catch (error: any) {
      this.error =
        error?.response?.data?.message === "EXPERT_ALREADY_MAPPED_TO_BRAND"
          ? "That expert is already mapped to this brand"
          : "Failed to add the expert"
      return false
    } finally {
      this.isActionLoading = false
    }
  }

  updateMapping = async (
    id: string,
    payload: { role?: BrandExpertRole; priority?: number; active?: boolean }
  ): Promise<boolean> => {
    this.isActionLoading = true
    this.error = null
    try {
      await knowledgeService.updateBrandExpert(id, payload)
      await this.loadMappings()
      this.invalidateBrandCaches()
      return true
    } catch (error) {
      this.error = "Failed to update the expert"
      return false
    } finally {
      this.isActionLoading = false
    }
  }

  removeMapping = async (id: string): Promise<boolean> => {
    this.isActionLoading = true
    this.error = null
    try {
      await knowledgeService.deleteBrandExpert(id)
      await this.loadMappings()
      this.invalidateBrandCaches()
      return true
    } catch (error) {
      this.error = "Failed to remove the expert"
      return false
    } finally {
      this.isActionLoading = false
    }
  }

  /**
   * Editing the mapping changes both which brands are selectable and who they
   * resolve to, so both caches have to go.
   */
  private invalidateBrandCaches = () => {
    this.brands = []
    this.resolutions = {}
  }
}

export default BrandExpertStore
